import React from 'react';
import { View, Text, ViewStyle, StyleSheet, Platform, StyleProp } from 'react-native';
import { useStyles } from '../../hooks/useStyles';
import { DesignTokens } from '../../shared/types/tokens';
import { useResponsive, getResponsiveValue } from '../../shared/utils/responsive';
import { addColorOpacity, createStyles } from '../../shared/utils/styles';

// ============================================================================
// Base Card Component
// ============================================================================

export interface BaseCardProps {
  children: React.ReactNode;
  style?: StyleProp<ViewStyle>;
  elevated?: boolean;
  padding?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  onPress?: () => void;
  testID?: string;
}

export function BaseCard({ 
  children, 
  style, 
  elevated = false, 
  padding = 'lg',
  onPress,
  testID 
}: BaseCardProps) {
  const { tokens, utils } = useStyles();
  const styles = makeBaseStyles(tokens, utils);

  const paddingValue = padding === 'none' ? 0 :
    padding === 'sm' ? utils.spacing.sm :
    padding === 'md' ? utils.spacing.md :
    padding === 'xl' ? utils.spacing.xl :
    padding === '2xl' ? utils.spacing['2xl'] :
    utils.spacing.lg; // default 'lg'

  const baseStyle: ViewStyle = {
    ...styles.base,
    padding: paddingValue,
    ...(elevated && styles.elevated),
    ...(onPress && styles.pressable),
  };

  // Combine base style with passed style, handling both objects and arrays
  const cardStyle = style 
    ? Array.isArray(style) 
      ? [baseStyle, ...style]
      : [baseStyle, style]
    : baseStyle;

  if (onPress) {
    const { Pressable } = require('react-native');
    return (
      <Pressable 
        style={cardStyle} 
        onPress={onPress}
        testID={testID}
        android_ripple={{ color: addColorOpacity(tokens.color.primary, 0.1) }}
      >
        {children}
      </Pressable>
    );
  }

  return (
    <View style={cardStyle} testID={testID}>
      {children}
    </View>
  );
}

// ============================================================================
// Section Card - Card with title and optional subtitle
// ============================================================================

export interface SectionCardProps extends Omit<BaseCardProps, 'children'> {
  title?: string;
  subtitle?: string;
  headerAction?: React.ReactNode;
  children: React.ReactNode;
}

export function SectionCard({
  title,
  subtitle,
  headerAction,
  children,
  style,
  ...baseProps
}: SectionCardProps) {
  const { tokens, utils } = useStyles();
  const responsive = useResponsive();
  const styles = makeSectionStyles(tokens, utils, responsive);

  return (
    <BaseCard style={style} {...baseProps}>
      {(title || subtitle || headerAction) && (
        <View style={styles.header}>
          <View style={styles.headerContent}>
            {title && (
              <Text style={styles.title}>{title}</Text>
            )}
            {subtitle && (
              <Text style={styles.subtitle}>{subtitle}</Text>
            )}
          </View>
          {headerAction && (
            <View style={styles.headerAction}>
              {headerAction}
            </View>
          )}
        </View>
      )}
      <View style={styles.content}>
        {children}
      </View>
    </BaseCard>
  );
}

// ============================================================================
// Detail Card - Card with key-value pairs
// ============================================================================

export interface DetailItem {
  label: string;
  value: string | number | React.ReactNode;
  highlight?: boolean;
  color?: string;
}

export interface DetailCardProps extends Omit<BaseCardProps, 'children'> {
  title?: string;
  items: DetailItem[];
  showDividers?: boolean;
}

export function DetailCard({
  title,
  items,
  showDividers = true,
  style,
  ...baseProps
}: DetailCardProps) {
  const { tokens, utils } = useStyles();
  const styles = makeDetailStyles(tokens, utils);

  return (
    <BaseCard style={style} {...baseProps}>
      {title && (
        <Text style={styles.title}>{title}</Text>
      )}
      <View style={styles.itemsContainer}>
        {items.map((item, index) => (
          <View key={index}>
            <View style={styles.itemRow}>
              <Text style={styles.label}>{item.label}</Text>
              <View style={styles.valueContainer}>
                {typeof item.value === 'string' || typeof item.value === 'number' ? (
                  <Text style={[
                    styles.value,
                    item.highlight && styles.valueHighlight,
                    item.color ? { color: item.color } : undefined
                  ].filter((s): s is NonNullable<typeof s> => Boolean(s))}>
                    {item.value}
                  </Text>
                ) : (
                  item.value
                )}
              </View>
            </View>
            {showDividers && index < items.length - 1 && (
              <View style={styles.divider} />
            )}
          </View>
        ))}
      </View>
    </BaseCard>
  );
}

// ============================================================================
// Summary Card - Card with statistics/metrics
// ============================================================================

export interface SummaryMetric {
  label: string;
  value: string | number | React.ReactNode;
  loading?: boolean;
  highlight?: boolean;
}

export interface SummaryCardProps extends Omit<BaseCardProps, 'children'> {
  title?: string;
  subtitle?: string;
  metrics: SummaryMetric[];
  columns?: 2 | 3 | 4;
}

export function SummaryCard({
  title,
  subtitle,
  metrics,
  columns = 3,
  style,
  ...baseProps
}: SummaryCardProps) {
  const { tokens, utils } = useStyles();
  const responsive = useResponsive();
  const styles = makeSummaryStyles(tokens, utils, responsive, columns);

  return (
    <BaseCard style={style} {...baseProps}>
      {(title || subtitle) && (
        <View style={styles.header}>
          {title && (
            <Text style={styles.title}>{title}</Text>
          )}
          {subtitle && (
            <Text style={styles.subtitle}>{subtitle}</Text>
          )}
        </View>
      )}
      <View style={styles.metricsContainer}>
        {metrics.map((metric, index) => (
          <React.Fragment key={index}>
            <View style={styles.metric}>
              {metric.loading ? (
                <View style={styles.loadingContainer}>
                  <View style={styles.loadingPlaceholder} />
                </View>
              ) : (
                <Text style={[
                  styles.metricValue,
                  metric.highlight && styles.metricValueHighlight
                ]}>
                  {metric.value}
                </Text>
              )}
              <Text style={styles.metricLabel}>{metric.label}</Text>
            </View>
            {index < metrics.length - 1 && (
              <View style={styles.metricDivider} />
            )}
          </React.Fragment>
        ))}
      </View>
    </BaseCard>
  );
}

// ============================================================================
// List Card - Card optimized for lists
// ============================================================================

export interface ListCardProps extends Omit<BaseCardProps, 'children'> {
  title?: string;
  subtitle?: string;
  headerAction?: React.ReactNode;
  emptyMessage?: string;
  emptySubmessage?: string;
  items: React.ReactNode[];
  loading?: boolean;
}

export function ListCard({
  title,
  subtitle,
  headerAction,
  emptyMessage,
  emptySubmessage,
  items,
  loading = false,
  style,
  ...baseProps
}: ListCardProps) {
  const { tokens, utils } = useStyles();
  const styles = makeListStyles(tokens, utils);

  return (
    <BaseCard style={style} {...baseProps}>
      {(title || subtitle || headerAction) && (
        <View style={styles.header}>
          <View style={styles.headerContent}>
            {title && (
              <Text style={styles.title}>{title}</Text>
            )}
            {subtitle && (
              <Text style={styles.subtitle}>{subtitle}</Text>
            )}
          </View>
          {headerAction && (
            <View style={styles.headerAction}>
              {headerAction}
            </View>
          )}
        </View>
      )}
      <View style={styles.listContainer}>
        {loading ? (
          <View style={styles.emptyState}>
            <Text style={styles.emptyText}>Loading...</Text>
          </View>
        ) : items.length === 0 ? (
          <View style={styles.emptyState}>
            {emptyMessage && (
              <Text style={styles.emptyMessage}>{emptyMessage}</Text>
            )}
            {emptySubmessage && (
              <Text style={styles.emptySubmessage}>{emptySubmessage}</Text>
            )}
          </View>
        ) : (
          <View style={styles.itemsList}>
            {items.map((item, index) => (
              <View key={index} style={styles.listItem}>
                {item}
              </View>
            ))}
          </View>
        )}
      </View>
    </BaseCard>
  );
}

// ============================================================================
// Filter Card - Card for filter controls
// ============================================================================

export interface FilterCardProps extends Omit<BaseCardProps, 'children'> {
  title?: string;
  subtitle?: string;
  children: React.ReactNode;
}

export function FilterCard({
  title,
  subtitle,
  children,
  style,
  ...baseProps
}: FilterCardProps) {
  const { tokens, utils } = useStyles();
  const styles = makeFilterStyles(tokens, utils);

  return (
    <BaseCard style={style} {...baseProps}>
      {(title || subtitle) && (
        <View style={styles.header}>
          {title && (
            <Text style={styles.title}>{title}</Text>
          )}
          {subtitle && (
            <Text style={styles.subtitle}>{subtitle}</Text>
          )}
        </View>
      )}
      <View style={styles.content}>
        {children}
      </View>
    </BaseCard>
  );
}

// ============================================================================
// Legacy Card - Backward compatibility
// ============================================================================

export interface CardProps extends BaseCardProps {}

export function Card(props: CardProps) {
  return <BaseCard {...props} />;
}

// ============================================================================
// Style Functions
// ============================================================================

function makeBaseStyles(tokens: DesignTokens, utils: ReturnType<typeof createStyles>) {
  return StyleSheet.create({
    base: {
      backgroundColor: tokens.color.surface,
      borderRadius: utils.radius.lg,
      borderWidth: 1,
      borderColor: tokens.color.border,
      ...Platform.select({
        web: {
          boxSizing: 'border-box' as any,
          transition: 'box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease',
        },
      }),
    },
    elevated: {
      backgroundColor: tokens.color.surfaceElevated || tokens.color.surface,
      borderColor: addColorOpacity(tokens.color.border, 0.5),
      ...Platform.select({
        web: {
          boxShadow: `0 4px 12px ${addColorOpacity('#000', 0.08)}, 0 1px 3px ${addColorOpacity('#000', 0.04)}`,
        } as any,
        default: {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: 0.08,
          shadowRadius: 12,
          elevation: 4,
        },
      }),
    },
    pressable: {
      ...Platform.select({
        web: {
          cursor: 'pointer',
          transition: 'all 0.2s ease',
        } as any,
      }),
    },
  });
}

function makeSectionStyles(
  tokens: DesignTokens, 
  utils: ReturnType<typeof createStyles>,
  responsive: ReturnType<typeof useResponsive>
) {
  const { isSmallMobile, isMobile } = responsive;
  
  // More responsive typography for mobile
  const headingSize = isSmallMobile ? 16 : getResponsiveValue(18, 20, 22, 24);
  const bodySize = isSmallMobile ? 12 : getResponsiveValue(13, 14, 15, 16);

  return StyleSheet.create({
    header: {
      flexDirection: isMobile ? 'column' : 'row',
      justifyContent: 'space-between',
      alignItems: isMobile ? 'stretch' : 'flex-start',
      marginBottom: isMobile ? utils.spacing.md : utils.spacing.lg,
      gap: isSmallMobile ? utils.spacing.xs : utils.spacing.md,
    },
    headerContent: {
      flex: isMobile ? 0 : 1,
      minWidth: 0,
    },
    headerAction: {
      marginLeft: isMobile ? 0 : utils.spacing.md,
      marginTop: isMobile ? utils.spacing.sm : 0,
    },
    title: {
      fontSize: headingSize,
      fontWeight: utils.typography.weights.bold,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.heading.fontFamily,
      lineHeight: headingSize * 1.2,
      marginBottom: utils.spacing.xs,
    },
    subtitle: {
      fontSize: bodySize,
      color: tokens.color.onBackground,
      fontFamily: utils.typography.body.fontFamily,
      lineHeight: bodySize * 1.5,
    },
    content: {
      width: '100%',
    },
  });
}

function makeDetailStyles(tokens: DesignTokens, utils: ReturnType<typeof createStyles>) {
  return StyleSheet.create({
    title: {
      fontSize: utils.typography.sizes.lg,
      fontWeight: utils.typography.weights.bold,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.heading.fontFamily,
      marginBottom: utils.spacing.md,
    },
    itemsContainer: {
      width: '100%',
    },
    itemRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: utils.spacing.sm,
    },
    label: {
      fontSize: utils.typography.sizes.base,
      color: addColorOpacity(tokens.color.onBackground, 0.8),
      fontFamily: utils.typography.body.fontFamily,
      flex: 1,
    },
    valueContainer: {
      flex: 1,
      alignItems: 'flex-end',
    },
    value: {
      fontSize: utils.typography.sizes.base,
      fontWeight: utils.typography.weights.medium,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.body.fontFamily,
    },
    valueHighlight: {
      fontWeight: utils.typography.weights.semibold,
    },
    divider: {
      height: 1,
      backgroundColor: tokens.color.border,
      marginVertical: utils.spacing.xs,
    },
  });
}

function makeSummaryStyles(
  tokens: DesignTokens,
  utils: ReturnType<typeof createStyles>,
  responsive: ReturnType<typeof useResponsive>,
  columns: number
) {
  const { isSmallMobile, isMobile } = responsive;
  
  // More responsive typography for mobile
  const headingSize = isSmallMobile ? 16 : getResponsiveValue(18, 20, 22, 24);
  const bodySize = isSmallMobile ? 12 : getResponsiveValue(13, 14, 15, 16);
  const metricValueSize = isSmallMobile ? 18 : getResponsiveValue(20, 22, 24, 26);

  return StyleSheet.create({
    header: {
      marginBottom: isMobile ? utils.spacing.md : utils.spacing.lg,
    },
    title: {
      fontSize: headingSize,
      fontWeight: utils.typography.weights.bold,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.heading.fontFamily,
      lineHeight: headingSize * 1.2,
      marginBottom: utils.spacing.xs,
    },
    subtitle: {
      fontSize: bodySize,
      color: tokens.color.onBackground,
      fontFamily: utils.typography.body.fontFamily,
      lineHeight: bodySize * 1.5,
    },
    metricsContainer: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'space-around',
      alignItems: 'center',
      gap: isSmallMobile ? utils.spacing.sm : utils.spacing.md,
      paddingVertical: isSmallMobile ? 0 : utils.spacing.sm,
    },
    metric: {
      flex: isSmallMobile ? 0 : 1,
      flexBasis: isSmallMobile ? '30%' : 'auto',
      alignItems: 'center',
      minWidth: isSmallMobile ? 60 : 0,
      paddingVertical: isSmallMobile ? utils.spacing.xs : 0,
    },
    metricValue: {
      fontSize: metricValueSize,
      fontWeight: utils.typography.weights.bold,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.heading.fontFamily,
      marginBottom: 2,
      textAlign: 'center',
      width: '100%',
    },
    metricValueHighlight: {
      color: tokens.color.primary,
    },
    metricLabel: {
      fontSize: isSmallMobile ? 10 : bodySize,
      color: tokens.color.onBackground,
      fontFamily: utils.typography.body.fontFamily,
      lineHeight: (isSmallMobile ? 10 : bodySize) * 1.3,
      textAlign: 'center',
      width: '100%',
    },
    metricDivider: {
      width: isSmallMobile ? 0 : 1,
      height: isSmallMobile ? 0 : 'auto',
      backgroundColor: isSmallMobile ? 'transparent' : tokens.color.border,
      marginVertical: utils.spacing.xs,
    },
    loadingContainer: {
      marginBottom: utils.spacing.xs,
      alignItems: 'center',
    },
    loadingPlaceholder: {
      width: isSmallMobile ? 30 : 40,
      height: isSmallMobile ? 18 : 24,
      backgroundColor: addColorOpacity(tokens.color.border, 0.3),
      borderRadius: utils.radius.sm,
    },
  });
}

function makeListStyles(tokens: DesignTokens, utils: ReturnType<typeof createStyles>) {
  // Get responsive values using window width
  let isSmallMobile = false;
  let isMobile = false;
  try {
    const width = Platform.OS === 'web' && typeof window !== 'undefined' 
      ? window.innerWidth 
      : require('react-native').Dimensions.get('window').width;
    isSmallMobile = width < 400;
    isMobile = width < 768;
  } catch (e) {
    // Fallback
  }
  
  const headingSize = isSmallMobile ? 16 : getResponsiveValue(18, 20, 22, 24);
  const bodySize = isSmallMobile ? 12 : getResponsiveValue(13, 14, 15, 16);

  return StyleSheet.create({
    header: {
      flexDirection: isMobile ? 'column' : 'row',
      justifyContent: 'space-between',
      alignItems: isMobile ? 'stretch' : 'flex-start',
      marginBottom: isMobile ? utils.spacing.md : utils.spacing.lg,
      gap: isSmallMobile ? utils.spacing.xs : utils.spacing.md,
    },
    headerContent: {
      flex: isMobile ? 0 : 1,
      minWidth: 0,
    },
    headerAction: {
      marginLeft: isMobile ? 0 : utils.spacing.md,
      marginTop: isMobile ? utils.spacing.sm : 0,
    },
    title: {
      fontSize: headingSize,
      fontWeight: utils.typography.weights.bold,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.heading.fontFamily,
      lineHeight: headingSize * 1.2,
      marginBottom: utils.spacing.xs,
    },
    subtitle: {
      fontSize: bodySize,
      color: tokens.color.onBackground,
      fontFamily: utils.typography.body.fontFamily,
      lineHeight: bodySize * 1.5,
    },
    listContainer: {
      width: '100%',
    },
    itemsList: {
      gap: isSmallMobile ? utils.spacing.xs : utils.spacing.sm,
    },
    listItem: {
      width: '100%',
    },
    emptyState: {
      paddingVertical: isMobile ? utils.spacing.xl : utils.spacing['2xl'],
      alignItems: 'center',
      justifyContent: 'center',
    },
    emptyMessage: {
      fontSize: headingSize,
      fontWeight: utils.typography.weights.bold,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.heading.fontFamily,
      textAlign: 'center',
      marginBottom: utils.spacing.sm,
    },
    emptyText: {
      fontSize: bodySize,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.body.fontFamily,
      textAlign: 'center',
    },
    emptySubmessage: {
      fontSize: bodySize,
      color: addColorOpacity(tokens.color.onSurface, 0.7),
      fontFamily: utils.typography.body.fontFamily,
      textAlign: 'center',
      lineHeight: bodySize * 1.5,
    },
  });
}

function makeFilterStyles(tokens: DesignTokens, utils: ReturnType<typeof createStyles>) {
  // Get responsive values using window width
  let isSmallMobile = false;
  let isMobile = false;
  try {
    const width = Platform.OS === 'web' && typeof window !== 'undefined' 
      ? window.innerWidth 
      : require('react-native').Dimensions.get('window').width;
    isSmallMobile = width < 400;
    isMobile = width < 768;
  } catch (e) {
    // Fallback
  }
  
  const headingSize = isSmallMobile ? 16 : getResponsiveValue(18, 20, 22, 24);
  const bodySize = isSmallMobile ? 12 : getResponsiveValue(13, 14, 15, 16);

  return StyleSheet.create({
    header: {
      marginBottom: isMobile ? utils.spacing.md : utils.spacing.lg,
    },
    title: {
      fontSize: headingSize,
      fontWeight: utils.typography.weights.bold,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.heading.fontFamily,
      lineHeight: headingSize * 1.2,
      marginBottom: utils.spacing.xs,
    },
    subtitle: {
      fontSize: bodySize,
      color: tokens.color.onBackground,
      fontFamily: utils.typography.body.fontFamily,
      lineHeight: bodySize * 1.5,
    },
    content: {
      width: '100%',
    },
  });
}
